import { Table } from "antd"
import React, { useState, useEffect, useRef, useMemo } from "react"
import styles from "./PricingTable.module.css"
import { Icon } from "ui/Icon"
import PropTypes from "prop-types"
import { columns } from "./Columns"
import { EditPricesForm } from "../EditPricesForm"
import { useSelector } from "react-redux"
import { modelSelector } from "store/selectors"
import { useDevice } from "hooks/useDevice"
import { durationFormatter } from "utils/durationFormatter"
import i18n from "i18n"
import { TRIP_PRICE } from "../../utils/constants"

function cellValue(value) {
  if (value === null || value === undefined) return "-"
  return `$${value}`
}

function MobilePricingCards({
  filteredData,
  pricingType,
  isFlatRate,
  expandedRowKeys,
  expandedRowData,
  setExpandedRowData,
  setExpandedRowKeys,
  setDraftPrices,
  addNewPrice,
  setPricingType,
  form,
  onPriceInteraction,
  closeEditForm
}) {
  return (
    <div className={styles.cardList}>
      {filteredData.map((record) => {
        const isExpanded = expandedRowKeys.includes(record.id)
        const recordPricingType = record.pricing_type || pricingType
        const isRecordPerPerson = recordPricingType === "charge_per_person"
        return (
          <div key={record.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardFields}>
                <div className={styles.cardField}>
                  <span className={styles.cardLabel}>
                    {i18n.t("experiences.scheduling.sections.length_and_rates.duration")}
                  </span>
                  <span className={styles.cardValue}>{durationFormatter(record[TRIP_PRICE.DURATION])}</span>
                </div>
                <div className={styles.cardField}>
                  <span className={styles.cardLabel}>
                    {i18n.t("experiences.scheduling.sections.length_and_rates.buffer_time")}
                  </span>
                  <span className={styles.cardValue}>{durationFormatter(record[TRIP_PRICE.BUFFER_TIME])}</span>
                </div>
                {!isRecordPerPerson && (
                  <div className={styles.cardField}>
                    <span className={styles.cardLabel}>
                      {i18n.t("experiences.scheduling.sections.length_and_rates.flat_rate")}
                    </span>
                    <span className={styles.cardValue}>{cellValue(record[TRIP_PRICE.FLAT_RATE])}</span>
                  </div>
                )}
                {isRecordPerPerson && (
                  <>
                    <div className={styles.cardField}>
                      <span className={styles.cardLabel}>
                        {i18n.t("experiences.scheduling.sections.length_and_rates.adults")}
                      </span>
                      <span className={styles.cardValue}>{cellValue(record[TRIP_PRICE.PRICE_ADULT])}</span>
                    </div>
                    <div className={styles.cardField}>
                      <span className={styles.cardLabel}>
                        {i18n.t("experiences.scheduling.sections.length_and_rates.children")}
                      </span>
                      <span className={styles.cardValue}>{cellValue(record[TRIP_PRICE.PRICE_CHILD])}</span>
                    </div>
                    <div className={styles.cardField}>
                      <span className={styles.cardLabel}>
                        {i18n.t("experiences.scheduling.sections.length_and_rates.infants")}
                      </span>
                      <span className={styles.cardValue}>{cellValue(record[TRIP_PRICE.PRICE_INFANT])}</span>
                    </div>
                    <div className={styles.cardField}>
                      <span className={styles.cardLabel}>
                        {i18n.t("experiences.scheduling.sections.length_and_rates.passengers")}
                      </span>
                      <span className={styles.cardValue}>{cellValue(record[TRIP_PRICE.PRICE_PASSENGER])}</span>
                    </div>
                  </>
                )}
              </div>
              <Icon
                type="feather"
                name={isExpanded ? "ChevronUp" : "ChevronDown"}
                color="currentColor"
                width={20}
                height={20}
                onClick={() => {
                  const nowExpanded = !isExpanded
                  setExpandedRowKeys(nowExpanded ? [record.id] : [])
                  setExpandedRowData(record)
                  if (record.pricing_type) setPricingType(record.pricing_type)
                }}
                className={styles.cardChevron}
              />
            </div>
            {isExpanded && (
              <div className={styles.cardEditForm}>
                <EditPricesForm
                  record={record}
                  key={record.id}
                  expandedRowData={expandedRowData}
                  isFlatRate={isFlatRate}
                  closeEditForm={closeEditForm}
                  setDraftPrices={setDraftPrices}
                  addNewPrice={addNewPrice}
                  setPricingType={setPricingType}
                  form={form}
                  onPriceInteraction={onPriceInteraction}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

MobilePricingCards.propTypes = {
  filteredData: PropTypes.arrayOf(PropTypes.object).isRequired,
  pricingType: PropTypes.string,
  isFlatRate: PropTypes.bool,
  expandedRowKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  expandedRowData: PropTypes.object,
  setExpandedRowData: PropTypes.func.isRequired,
  setExpandedRowKeys: PropTypes.func.isRequired,
  setDraftPrices: PropTypes.func.isRequired,
  addNewPrice: PropTypes.func.isRequired,
  setPricingType: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  onPriceInteraction: PropTypes.func,
  closeEditForm: PropTypes.func.isRequired
}

export const PricingTable = ({
  draftPrices,
  setDraftPrices,
  addNewPrice,
  setPricingType,
  isFlatRate,
  pricingType,
  expandedRowData,
  setExpandedRowData,
  form,
  onPriceInteraction
}) => {
  const { trip_prices = [] } = useSelector(modelSelector("trip"))
  const { isMobile } = useDevice()
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const lastExpandedId = useRef(null)
  const combinedData = [...trip_prices, ...draftPrices]
  const filteredData = pricingType
    ? combinedData.filter(
      (item) => item.pricing_type === pricingType || !item.pricing_type
    )
    : combinedData

  const closeEditForm = () => {
    setExpandedRowKeys([])
    setExpandedRowData({})
  }

  useEffect(() => {
    if (draftPrices.length > 0) {
      const newestRow = draftPrices[draftPrices.length - 1]
      if (newestRow.id !== lastExpandedId.current) {
        setExpandedRowKeys([newestRow.id])
        if (newestRow.pricing_type) {
          setPricingType(newestRow.pricing_type)
        }
        lastExpandedId.current = newestRow.id
      }
    }
  }, [draftPrices, trip_prices, setPricingType])

  const filteredColumns = useMemo(() => {
    if (pricingType === "charge_per_person") {
      return columns.filter(col => col.key !== "flat_rate");
    }
    return columns;
  }, [pricingType]);

  if (isMobile) {
    return (
      <MobilePricingCards
        filteredData={filteredData}
        pricingType={pricingType}
        isFlatRate={isFlatRate}
        expandedRowKeys={expandedRowKeys}
        expandedRowData={expandedRowData}
        setExpandedRowData={setExpandedRowData}
        setExpandedRowKeys={setExpandedRowKeys}
        setDraftPrices={setDraftPrices}
        addNewPrice={addNewPrice}
        setPricingType={setPricingType}
        form={form}
        onPriceInteraction={onPriceInteraction}
        closeEditForm={closeEditForm}
      />
    )
  }

  return (
    <div className={styles.responsiveTableContainer}>
      <Table
        dataSource={filteredData}
        columns={filteredColumns}
        expandable={{
          expandedRowKeys,
          expandedRowRender: (record) => (
            <EditPricesForm
              record={record}
              key={record.id}
              expandedRowData={expandedRowData}
              isFlatRate={isFlatRate}
              closeEditForm={closeEditForm}
              setDraftPrices={setDraftPrices}
              addNewPrice={addNewPrice}
              setPricingType={setPricingType}
              form={form}
              onPriceInteraction={onPriceInteraction}
            />
          ),
          rowExpandable: () => true,
          expandedRowClassName: () => styles.expandedRow,
          expandIcon: ({ expanded, onExpand, record }) => (
            <Icon
              type="feather"
              name={expanded ? "ChevronUp" : "ChevronDown"}
              color="currentColor"
              width={20}
              height={20}
              onClick={(e) => onExpand(record, e)}
            />
          ),
          onExpand: (expanded, record) => {
            setExpandedRowKeys(expanded ? [record.id] : [])
            setExpandedRowData(record)
            if (record.pricing_type) {
              setPricingType(record.pricing_type)
            }
          }
        }}
        pagination={false}
        rowHoverable={false}
        size="middle"
        className={styles.customTable}
        rowClassName={styles.customTableRow}
        rowKey="id"
      />

    </div>
  )
}

PricingTable.propTypes = {
  draftPrices: PropTypes.arrayOf(PropTypes.object).isRequired,
  setDraftPrices: PropTypes.func.isRequired,
  addNewPrice: PropTypes.func.isRequired,
  setPricingType: PropTypes.func.isRequired,
  isFlatRate: PropTypes.bool,
  pricingType: PropTypes.string,
  form: PropTypes.object.isRequired,
  expandedRowData: PropTypes.object,
  setExpandedRowData: PropTypes.func,
  onPriceInteraction: PropTypes.func
}
